// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
// Presentation-only, trusted packaged imagery. Player drawing programs cannot name URLs or files.
import catalog from '../../machines/catalog.json'
import { coastalPrint, prepareCoastalPrint, paintPrintPixels } from './coastalPrintMasters'
import { prepareAuthoredHardware } from './authoredHardware'
import { prepareAuthoredRamps } from './authoredRamps'
import { prepareAuthoredDisplays } from './authoredDisplays'
import { prepareAuthoredCabinets } from './authoredCabinets'
import { prepareAuthoredLowerAssembly } from './authoredLowerAssembly'
import { prepareSlingAssembly } from './slingAssembly'
import { prepareAuthoredWires } from './authoredWires'
import { prepareInsertOptics } from './insertOptics'
import { prepareApronAssembly } from './apronAssembly'
import { prepareToyAssembly } from './toyAssembly'
import { prepareFlasherAssembly } from './flasherAssembly'
import { prepareCabinetControlAssembly } from './cabinetControlAssembly'
import { prepareAuthoredGuides } from './authoredGuides'
import { prepareAuthoredFlippers } from './authoredFlippers'
import { prepareAuthoredPosts } from './authoredPosts'
import { prepareAuthoredSpinners } from './authoredSpinners'
import { prepareAuthoredGlazing } from './authoredGlazing'
import { prepareAuthoredBackbox } from './authoredBackbox'
import { prepareContactCoating } from './contactCoating'
import { prepareFloorContact } from './floorContact'
import { prepareCabinetLegs } from './cabinetLegs'
import {prepareCabinetTrim} from './cabinetTrim'
import {prepareSalvageMechanisms} from './salvageMechanisms'

/** Load only the selected coastal triplet; original machines download no replacement illustrations. */
export async function preparePackagedArtwork(definition: unknown): Promise<void> {
  if((definition as {toys?:{kind:string}[]}).toys?.some(t=>t.kind==='hull'||t.kind==='portal'))await Promise.all([prepareSalvageMechanisms(),prepareCoastalPrint(3)])
  await Promise.all([prepareSlingAssembly(),prepareAuthoredRamps(),prepareAuthoredGuides(),prepareAuthoredPosts(),prepareAuthoredWires(),prepareInsertOptics(),prepareApronAssembly(),prepareToyAssembly(),prepareFlasherAssembly(),prepareCabinetControlAssembly(),prepareCabinetLegs(),prepareCabinetTrim()])
  const d = definition as { machineId?: string; machineVersion?: string }
  const entry = catalog.machines.find(e => e.id === d.machineId && e.version === d.machineVersion && 'illustration' in e)
  if (entry === undefined || !('illustration' in entry)) return
  // Publish the selected new machine only after its unchanged prints and meshes are ready.
  // The remaining coastal shell/print assets are needed only for these three machines.
  await Promise.all([prepareCoastalPrint(entry.illustration.tile),
    prepareAuthoredHardware(), prepareAuthoredRamps(), prepareAuthoredDisplays(), prepareAuthoredCabinets(), prepareAuthoredLowerAssembly(), prepareAuthoredGuides(), prepareAuthoredFlippers(), prepareAuthoredPosts(), prepareAuthoredSpinners(), prepareAuthoredGlazing(), prepareAuthoredBackbox(), prepareContactCoating(), prepareFloorContact()])
}

export function packagedIllustration(id: string, version: string): number | undefined {
  const entry = catalog.machines.find(e => e.id === id && e.version === version)
  if (entry === undefined || !('illustration' in entry)) return undefined
  const tile = entry.illustration.tile
  return coastalPrint(tile, 'backglass') !== undefined && coastalPrint(tile, 'playfield') !== undefined && coastalPrint(tile, 'cabinet') !== undefined ? tile : undefined
}

export function paintPackagedIllustration(ctx: CanvasRenderingContext2D, width: number, height: number, tile: number, physicalAspect?: number, cabinet = false, mirror = false, front = false): void {
  const separate = tile === 3 && cabinet
  const master = coastalPrint(tile, separate && front ? 'front' : separate && mirror ? 'cabinetRight' : cabinet ? 'cabinet' : 'backglass')
  if (master === undefined) throw new Error('Coastal print must be verified and prepared before painting')
  paintPrintPixels(ctx, width, height, master, false, physicalAspect, separate ? false : mirror, !cabinet)
}

export function paintPackagedPlayfield(ctx: CanvasRenderingContext2D, width: number, height: number, tile: number, physicalAspect?: number): void {
  const master = coastalPrint(tile, 'playfield')
  if (master === undefined) throw new Error('Coastal print must be verified and prepared before painting')
  paintPrintPixels(ctx, width, height, master, true, physicalAspect, false, false, true)
}
